import { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const DEFAULT_FORM = {
  name: "",
  startAt: "",
  endAt: "",
  isActive: false,
};

function getSeasonsCollectionRef() {
  return collection(db, "config", "game", "season");
}

function getSeasonDocRef(seasonNumber) {
  return doc(db, "config", "game", "season", String(seasonNumber));
}

function getGameConfigRef() {
  return doc(db, "config", "game");
}

function formatDate(value) {
  if (!value) return "—";

  const date = value.toDate ? value.toDate() : new Date(value);

  return date.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function timestampToInputValue(value) {
  if (!value) return "";

  const date = value.toDate ? value.toDate() : new Date(value);

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);

  return localDate.toISOString().slice(0, 16);
}

function inputValueToTimestamp(value) {
  if (!value) return null;

  return Timestamp.fromDate(new Date(value));
}

function getTimeLeft(endAt) {
  if (!endAt) {
    return {
      expired: false,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      hasEnd: false,
    };
  }

  const endDate = endAt.toDate ? endAt.toDate() : new Date(endAt);
  const difference = endDate.getTime() - Date.now();

  if (difference <= 0) {
    return {
      expired: true,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      hasEnd: true,
    };
  }

  return {
    expired: false,
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
    hasEnd: true,
  };
}

function SeasonCountdown({ endAt }) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(endAt));

  useEffect(() => {
    setTimeLeft(getTimeLeft(endAt));

    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(endAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [endAt]);

  if (!timeLeft.hasEnd) {
    return (
      <div className="season-countdown season-countdown--empty">
        <strong>Sin fecha</strong>
        <span>No hay fecha de fin configurada</span>
      </div>
    );
  }

  if (timeLeft.expired) {
    return (
      <div className="season-countdown season-countdown--expired">
        <strong>Temporada finalizada</strong>
      </div>
    );
  }

  return (
    <div className="season-countdown">
      <div>
        <strong>{timeLeft.days}</strong>
        <span>Días</span>
      </div>

      <div>
        <strong>{timeLeft.hours}</strong>
        <span>Horas</span>
      </div>

      <div>
        <strong>{timeLeft.minutes}</strong>
        <span>Min</span>
      </div>

      <div>
        <strong>{timeLeft.seconds}</strong>
        <span>Seg</span>
      </div>
    </div>
  );
}

function SeasonSwitch({ checked, onChange, disabled }) {
  return (
    <label className="switch-row">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="switch-ui"></span>
      <strong>{checked ? "Activa" : "Inactiva"}</strong>
    </label>
  );
}

function SeasonsPage() {
  const [seasons, setSeasons] = useState([]);
  const [minigames, setMinigames] = useState([]);

  const [currentSeason, setCurrentSeason] = useState(null);
  const [currentSeasonEndAt, setCurrentSeasonEndAt] = useState(null);

  const [topModalSeason, setTopModalSeason] = useState(null);
  const [selectedMinigameId, setSelectedMinigameId] = useState("");
  const [topScores, setTopScores] = useState([]);
  const [loadingTopScores, setLoadingTopScores] = useState(false);

  const [editingSeason, setEditingSeason] = useState(null);
  const [editForm, setEditForm] = useState(DEFAULT_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const nextSeasonNumber = useMemo(() => {
    if (seasons.length === 0) return 0;

    const maxNumber = Math.max(
      ...seasons.map((season) => Number(season.number) || 0)
    );

    return maxNumber + 1;
  }, [seasons]);

  const currentSeasonData = useMemo(() => {
    if (currentSeason === null) return null;

    return (
      seasons.find(
        (season) => Number(season.number) === Number(currentSeason)
      ) || null
    );
  }, [seasons, currentSeason]);

  const effectiveCurrentEndAt =
    currentSeasonEndAt || currentSeasonData?.endAt || null;

  const filteredSeasons = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return seasons.filter((season) => {
      const name = String(season.name || "").toLowerCase();
      const number = String(season.number ?? "").toLowerCase();

      return name.includes(normalizedSearch) || number.includes(normalizedSearch);
    });
  }, [seasons, searchText]);

  async function loadGameConfig() {
    const configSnapshot = await getDoc(getGameConfigRef());

    if (!configSnapshot.exists()) {
      setCurrentSeason(null);
      setCurrentSeasonEndAt(null);
      return;
    }

    const data = configSnapshot.data();

    setCurrentSeason(data.currentSeason ?? null);
    setCurrentSeasonEndAt(
      data.currentSeasonEndAt ||
        data.currentSeasonEndDate ||
        data.seasonEndDate ||
        null
    );
  }

  async function loadSeasons() {
    const seasonsQuery = query(
      getSeasonsCollectionRef(),
      orderBy("number", "asc")
    );

    const snapshot = await getDocs(seasonsQuery);

    const data = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    setSeasons(data);
  }

  async function loadMinigames() {
    const snapshot = await getDocs(collection(db, "minigames"));

    const data = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    setMinigames(data);
  }

  async function loadPageData() {
    try {
      setLoading(true);

      await Promise.all([loadGameConfig(), loadSeasons(), loadMinigames()]);
    } catch (error) {
      console.error("Error cargando temporadas:", error);
      alert("No se pudieron cargar las temporadas.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPageData();
  }, []);

  function openCreateModal() {
    setForm({
      name: "",
      startAt: timestampToInputValue(new Date()),
      endAt: "",
      isActive: false,
    });

    setIsCreateModalOpen(true);
  }

  function closeCreateModal() {
    if (saving) return;

    setIsCreateModalOpen(false);
    setForm(DEFAULT_FORM);
  }

  function updateForm(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function updateEditForm(field, value) {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function validateSeasonData(data) {
    if (!data.name.trim()) {
      alert("Ingresa el nombre de la temporada.");
      return false;
    }

    if (!data.startAt) {
      alert("Selecciona la fecha de inicio.");
      return false;
    }

    if (!data.endAt) {
      alert("Selecciona la fecha de fin.");
      return false;
    }

    const startDate = new Date(data.startAt);
    const endDate = new Date(data.endAt);

    if (endDate <= startDate) {
      alert("La fecha de fin debe ser posterior a la fecha de inicio.");
      return false;
    }

    return true;
  }

  async function handleCreateSeason(e) {
    e.preventDefault();

    if (!validateSeasonData(form)) return;

    const seasonNumber = nextSeasonNumber;

    try {
      setSaving(true);

      const seasonData = {
        name: form.name.trim(),
        number: seasonNumber,
        startAt: inputValueToTimestamp(form.startAt),
        endAt: inputValueToTimestamp(form.endAt),
        isActive: Boolean(form.isActive),
      };

      await setDoc(getSeasonDocRef(seasonNumber), seasonData);

      setSeasons((prev) =>
        [
          ...prev,
          {
            id: String(seasonNumber),
            ...seasonData,
          },
        ].sort((a, b) => Number(a.number) - Number(b.number))
      );

      closeCreateModal();

      alert("Temporada creada correctamente.");
    } catch (error) {
      console.error("Error creando temporada:", error);
      alert("No se pudo crear la temporada.");
    } finally {
      setSaving(false);
    }
  }

  function openEditModal(season) {
    setEditingSeason(season);

    setEditForm({
      name: season.name || "",
      startAt: timestampToInputValue(season.startAt),
      endAt: timestampToInputValue(season.endAt),
      isActive: season.isActive ?? false,
    });
  }

  function closeEditModal() {
    if (saving) return;

    setEditingSeason(null);
    setEditForm(DEFAULT_FORM);
  }

  async function handleUpdateSeason(e) {
    e.preventDefault();

    if (!editingSeason) return;

    if (!validateSeasonData(editForm)) return;

    try {
      setSaving(true);

      const seasonNumber = editingSeason.number;

      const updatedData = {
        name: editForm.name.trim(),
        number: Number(seasonNumber),
        startAt: inputValueToTimestamp(editForm.startAt),
        endAt: inputValueToTimestamp(editForm.endAt),
        isActive: Boolean(editForm.isActive),
      };

      await updateDoc(getSeasonDocRef(seasonNumber), updatedData);

      setSeasons((prev) =>
        prev.map((season) =>
          Number(season.number) === Number(seasonNumber)
            ? {
                ...season,
                ...updatedData,
              }
            : season
        )
      );

      if (Number(currentSeason) === Number(seasonNumber)) {
        setCurrentSeasonEndAt(updatedData.endAt);
      }

      closeEditModal();

      alert("Temporada actualizada correctamente.");
    } catch (error) {
      console.error("Error actualizando temporada:", error);
      alert("No se pudo actualizar la temporada.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteSeason(season) {
    const typed = window.prompt(
      `Vas a eliminar la temporada:\n\n${season.name}\nNúmero: ${season.number}\n\nPara confirmar, escribe el número de temporada:`
    );

    if (typed === null) return;

    if (String(typed).trim() !== String(season.number)) {
      alert("Confirmación incorrecta. No se eliminó la temporada.");
      return;
    }

    const finalConfirm = window.confirm(
      "¿Seguro? Esto eliminará el documento de temporada, pero NO eliminará los scores históricos."
    );

    if (!finalConfirm) return;

    try {
      setSaving(true);

      await deleteDoc(getSeasonDocRef(season.number));

      setSeasons((prev) =>
        prev.filter((item) => Number(item.number) !== Number(season.number))
      );

      alert("Temporada eliminada correctamente.");
    } catch (error) {
      console.error("Error eliminando temporada:", error);
      alert("No se pudo eliminar la temporada.");
    } finally {
      setSaving(false);
    }
  }

  function openTopModal(season) {
    setTopModalSeason(season);
    setTopScores([]);

    const firstMinigameId = minigames[0]?.id || "";
    setSelectedMinigameId(firstMinigameId);

    if (firstMinigameId) {
      loadTopScores(season.number, firstMinigameId);
    }
  }

  function closeTopModal() {
    setTopModalSeason(null);
    setSelectedMinigameId("");
    setTopScores([]);
  }

  async function loadTopScores(seasonNumber, minigameId) {
    if (!minigameId) return;

    try {
      setLoadingTopScores(true);

      const scoreCollectionName = `scores${seasonNumber}`;

      const scoresQuery = query(
        collection(db, "minigames", minigameId, scoreCollectionName),
        orderBy("bestScore", "desc"),
        limit(20)
      );

      const snapshot = await getDocs(scoresQuery);

      const data = snapshot.docs.map((docItem) => {
        const scoreData = docItem.data();

        return {
          id: docItem.id,
          uid: scoreData.uid || docItem.id,
          displayName: scoreData.displayName || "Sin nombre",
          bestScore: scoreData.bestScore ?? 0,
          lastScore: scoreData.lastScore ?? 0,
          updatedAt: scoreData.updatedAt || null,
          seasonNumber: scoreData.seasonNumber ?? seasonNumber,
        };
      });

      setTopScores(data);
    } catch (error) {
      console.error("Error cargando top scores:", error);
      alert("No se pudo cargar el top de jugadores.");
    } finally {
      setLoadingTopScores(false);
    }
  }

  function handleMinigameChange(value) {
    setSelectedMinigameId(value);

    if (topModalSeason) {
      loadTopScores(topModalSeason.number, value);
    }
  }

  const selectedMinigame = minigames.find(
    (minigame) => minigame.id === selectedMinigameId
  );

  return (
    <>
      <section className="admin-section-card seasons-page">
        <div className="section-header seasons-header">
          <div>
            <span className="section-eyebrow">Competición</span>
            <h2>Temporadas</h2>
            <p>Administra temporadas y consulta los top scores por minijuego.</p>
          </div>

          <div className="section-actions">
            <button
              className="primary-action-button"
              onClick={openCreateModal}
              disabled={saving}
            >
              ＋ Añadir temporada
            </button>

            <button
              className="secondary-button"
              onClick={loadPageData}
              disabled={saving}
            >
              🔄 Actualizar
            </button>

            <span className="section-count">
              {filteredSeasons.length} / {seasons.length} temporadas
            </span>
          </div>
        </div>

        <div className="season-current-card">
          <div>
            <span className="season-current-card__label">
              Temporada actual
            </span>

            <strong>
              {currentSeason !== null
                ? currentSeasonData?.name || `Season ${currentSeason}`
                : "No configurada"}
            </strong>

            <p>
              Número actual en config/game:{" "}
              {currentSeason !== null ? currentSeason : "—"}
            </p>

            <p>
              Fin:{" "}
              {effectiveCurrentEndAt
                ? formatDate(effectiveCurrentEndAt)
                : "No configurado"}
            </p>
          </div>

          <SeasonCountdown endAt={effectiveCurrentEndAt} />
        </div>

        <div className="users-toolbar">
          <div className="search-box users-search-box">
            <span>🔎</span>

            <input
              type="text"
              placeholder="Buscar por nombre o número de temporada..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {searchText && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setSearchText("")}
            >
              Limpiar
            </button>
          )}
        </div>

        {loading ? (
          <main className="users-loading-state">
            <section className="loading-card">
              <div className="banter-loader" aria-hidden="true">
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
              </div>

              <h2>Cargando dashboard...</h2>
              <p>Consultando usuarios, minijuegos, tienda, misiones y temporada.</p>
            </section>
          </main>
        ) : (
          <div className="table-wrapper">
            <table className="admin-table seasons-table">
              <thead>
                <tr>
                  <th>Temporada</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Score Path</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {filteredSeasons.map((season) => {
                  const isCurrent =
                    currentSeason !== null &&
                    Number(currentSeason) === Number(season.number);

                  return (
                    <tr key={season.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-name-row">
                            <span className="user-avatar">
                              {String(season.number ?? "?")}
                            </span>

                            <div>
                              <strong>
                                {season.name || `Season ${season.number}`}
                              </strong>

                              {isCurrent && (
                                <span className="status-badge status-badge--enabled">
                                  Actual
                                </span>
                              )}
                            </div>
                          </div>

                          <span>Temporada #{season.number}</span>
                        </div>
                      </td>

                      <td>{formatDate(season.startAt)}</td>

                      <td>{formatDate(season.endAt)}</td>

                      <td>
                        <code className="table-code">
                          scores{season.number}
                        </code>
                      </td>

                      <td>
                        <span
                          className={
                            season.isActive
                              ? "status-badge status-badge--enabled"
                              : "status-badge status-badge--disabled"
                          }
                        >
                          {season.isActive ? "Activa" : "Inactiva"}
                        </span>
                      </td>

                      <td>
                        <div className="table-actions">
                          <button
                            className="small-button"
                            onClick={() => openTopModal(season)}
                            disabled={minigames.length === 0}
                          >
                            Ver top
                          </button>

                          <button
                            className="small-button small-button--dark"
                            onClick={() => openEditModal(season)}
                            disabled={saving}
                          >
                            Editar
                          </button>

                          <button
                            className="small-button small-button--danger"
                            onClick={() => deleteSeason(season)}
                            disabled={saving || isCurrent}
                            title={
                              isCurrent
                                ? "No se recomienda eliminar la temporada actual"
                                : "Eliminar temporada"
                            }
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredSeasons.length === 0 && (
              <p className="empty-state">No hay temporadas para mostrar.</p>
            )}
          </div>
        )}
      </section>

      {isCreateModalOpen && (
        <div className="modal-backdrop">
          <div className="edit-modal">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Nueva temporada</span>
                <h3>Añadir temporada</h3>
                <p>
                  Se creará automáticamente como temporada #{nextSeasonNumber}.
                </p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeCreateModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateSeason} className="edit-form">
              <label>
                Número de temporada
                <input type="number" value={nextSeasonNumber} disabled />
                <small className="field-hint">
                  Se calcula automáticamente tomando el mayor número existente + 1.
                </small>
              </label>

              <label>
                Nombre
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateForm("name", e.target.value)}
                  placeholder="Season 1"
                  disabled={saving}
                />
              </label>

              <label>
                Fecha de inicio
                <input
                  type="datetime-local"
                  value={form.startAt}
                  onChange={(e) => updateForm("startAt", e.target.value)}
                  disabled={saving}
                />
              </label>

              <label>
                Fecha de fin
                <input
                  type="datetime-local"
                  value={form.endAt}
                  onChange={(e) => updateForm("endAt", e.target.value)}
                  disabled={saving}
                />
                <small className="field-hint">
                  Esta fecha puede copiarse a config/game como currentSeasonEndAt
                  cuando la temporada esté activa.
                </small>
              </label>

              <SeasonSwitch
                checked={form.isActive}
                onChange={(value) => updateForm("isActive", value)}
                disabled={saving}
              />

              <div className="edit-modal__actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeCreateModal}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="primary-action-button"
                  disabled={saving}
                >
                  {saving ? "Creando..." : "Crear temporada"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingSeason && (
        <div className="modal-backdrop">
          <div className="edit-modal">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Editar</span>
                <h3>Editar temporada</h3>
                <p>Temporada #{editingSeason.number}</p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeEditModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateSeason} className="edit-form">
              <label>
                Número de temporada
                <input type="number" value={editingSeason.number} disabled />
                <small className="field-hint">
                  No se puede cambiar porque define la colección de scores.
                </small>
              </label>

              <label>
                Nombre
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => updateEditForm("name", e.target.value)}
                  disabled={saving}
                />
              </label>

              <label>
                Fecha de inicio
                <input
                  type="datetime-local"
                  value={editForm.startAt}
                  onChange={(e) => updateEditForm("startAt", e.target.value)}
                  disabled={saving}
                />
              </label>

              <label>
                Fecha de fin
                <input
                  type="datetime-local"
                  value={editForm.endAt}
                  onChange={(e) => updateEditForm("endAt", e.target.value)}
                  disabled={saving}
                />
              </label>

              <SeasonSwitch
                checked={editForm.isActive}
                onChange={(value) => updateEditForm("isActive", value)}
                disabled={saving}
              />

              <div className="edit-modal__actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeEditModal}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="primary-action-button"
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {topModalSeason && (
        <div className="modal-backdrop">
          <div className="edit-modal edit-modal--wide">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Ranking</span>
                <h3>Top jugadores</h3>
                <p>
                  {topModalSeason.name || `Season ${topModalSeason.number}`} ·
                  scores{topModalSeason.number}
                </p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeTopModal}
              >
                ×
              </button>
            </div>

            <div className="top-score-toolbar">
              <label>
                Minijuego
                <select
                  value={selectedMinigameId}
                  onChange={(e) => handleMinigameChange(e.target.value)}
                >
                  {minigames.map((minigame) => (
                    <option key={minigame.id} value={minigame.id}>
                      {minigame.title || minigame.id} ({minigame.id})
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                className="secondary-button"
                onClick={() =>
                  loadTopScores(topModalSeason.number, selectedMinigameId)
                }
                disabled={!selectedMinigameId || loadingTopScores}
              >
                {loadingTopScores ? "Cargando..." : "Actualizar top"}
              </button>
            </div>

            <div className="top-score-context">
              <strong>{selectedMinigame?.title || selectedMinigameId}</strong>
              <span>
                Ruta: minigames/{selectedMinigameId}/scores
                {topModalSeason.number}
              </span>
            </div>

            {loadingTopScores ? (
              <div className="users-loading-state">
                <div className="mini-loader"></div>
                <strong>Cargando top de jugadores...</strong>
                <span>Consultando scores de la temporada.</span>
              </div>
            ) : topScores.length === 0 ? (
              <p className="empty-state">No hay scores para este minijuego.</p>
            ) : (
              <div className="table-wrapper">
                <table className="admin-table top-score-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Jugador</th>
                      <th>Best Score</th>
                      <th>Last Score</th>
                      <th>Actualizado</th>
                    </tr>
                  </thead>

                  <tbody>
                    {topScores.map((player, index) => (
                      <tr key={player.id}>
                        <td>
                          <strong>{index + 1}</strong>
                        </td>

                        <td>
                          <div className="user-cell">
                            <strong>{player.displayName}</strong>
                            <span>{player.uid}</span>
                          </div>
                        </td>

                        <td>{player.bestScore}</td>

                        <td>{player.lastScore}</td>

                        <td>{formatDate(player.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default SeasonsPage;